import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { StoryState } from "@/lib/story-store";

interface Props {
  state: StoryState;
  onUpdate: (partial: Partial<StoryState>) => void;
  onNext: () => void;
  onBack: () => void;
}

const temperaments = [
  { id: "calm", label: "Спокойный", desc: "Любит наблюдать и размышлять" },
  { id: "active", label: "Активный", desc: "Полон энергии и движения" },
  { id: "sensitive", label: "Чувствительный", desc: "Тонко чувствует эмоции" },
  { id: "social", label: "Общительный", desc: "Легко идёт на контакт" },
];

const ChildProfile = ({ state, onUpdate, onNext, onBack }: Props) => {
  const canContinue = state.name.trim() && state.temperament;

  // If goal (archetype) is already selected, use its accent color
  const goalColor = state.goal ? `var(--archetype-${state.goal})` : null;

  const selectedBorder = (isSelected: boolean) => {
    if (!isSelected) return {};
    if (goalColor) {
      return {
        borderColor: `hsl(${goalColor})`,
        backgroundColor: `hsl(${goalColor} / 0.06)`,
      };
    }
    return {};
  };

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-10 md:py-16 relative">
      <button
        onClick={onBack}
        className="fixed top-4 left-4 z-50 w-9 h-9 rounded-full flex items-center justify-center text-foreground bg-card/70 backdrop-blur-sm hover:bg-card transition-colors"
        aria-label="Назад"
      >
        <ChevronLeft size={20} />
      </button>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg space-y-8"
      >
        {/* Header */}
        <div className="space-y-2">
            <h2 className="text-2xl md:text-3xl font-serif text-foreground text-center w-full">
              Немного познакомимся
            </h2>
          <p className="text-sm text-muted-foreground font-sans text-center">
            Это поможет создать историю именно для вашего ребёнка
          </p>
        </div>

        {/* Имя */}
        <div className="space-y-2">
          <label className="font-sans font-semibold text-foreground" style={{ fontSize: "16px" }}>
            Имя ребёнка
          </label>
          <input
            type="text"
            value={state.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="Как его или её зовут?"
            className="w-full h-12 px-4 rounded-2xl bg-card border text-foreground font-sans focus:outline-none transition-shadow placeholder:text-muted-foreground/50"
            style={{ borderColor: "#E6E2DB" }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = goalColor
                ? `0 0 0 3px hsl(${goalColor} / 0.15)`
                : "0 0 0 3px hsl(var(--ring) / 0.2)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Обращение */}
        <div className="space-y-2">
          <label className="font-sans font-semibold text-foreground" style={{ fontSize: "16px" }}>
            Как обращаться в истории?
          </label>
          <div className="flex gap-2">
            {([{ id: "he", label: "Он" }, { id: "she", label: "Она" }] as const).map((g) => {
              const isSelected = state.gender === g.id;
              return (
                <motion.button
                  key={g.id}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onUpdate({ gender: g.id })}
                  className={`h-11 px-6 rounded-full font-sans font-semibold text-sm transition-all ${
                    isSelected
                      ? "border-2 border-foreground/70"
                      : "border border-transparent hover:border-border"
                  }`}
                  style={{
                    backgroundColor: isSelected
                      ? goalColor ? `hsl(${goalColor} / 0.06)` : "hsl(var(--foreground) / 0.06)"
                      : "#F1EEE9",
                    color: "#2F2F2F",
                    ...(isSelected && goalColor ? { borderColor: `hsl(${goalColor} / 0.5)` } : {}),
                  }}
                >
                  {g.label}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Возраст */}
        <div className="space-y-2">
          <label className="font-sans font-semibold text-foreground" style={{ fontSize: "16px" }}>
            Возраст
          </label>
          <p className="text-xs text-muted-foreground font-sans">Выберите один вариант</p>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {[3, 4, 5, 6, 7, 8, 9].map((age) => {
              const isSelected = state.age === age;
              return (
                <motion.button
                  key={age}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onUpdate({ age })}
                  className={`shrink-0 h-11 w-11 rounded-full font-sans font-semibold text-sm transition-all ${
                    isSelected
                      ? "border-2 border-foreground/70"
                      : "border border-transparent hover:border-border"
                  }`}
                  style={{
                    backgroundColor: isSelected
                      ? goalColor ? `hsl(${goalColor} / 0.06)` : "hsl(var(--foreground) / 0.06)"
                      : "#F1EEE9",
                    color: "#2F2F2F",
                    ...(isSelected && goalColor ? { borderColor: `hsl(${goalColor} / 0.5)` } : {}),
                  }}
                >
                  {age}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Темперамент */}
        <div className="space-y-2">
          <label className="font-sans font-semibold text-foreground" style={{ fontSize: "16px" }}>
            Темперамент
          </label>
          <p className="text-xs text-muted-foreground font-sans">Как бы вы его описали?</p>
          <div className="grid grid-cols-2 gap-3">
            {temperaments.map((t) => {
              const isSelected = state.temperament === t.id;
              return (
                <motion.button
                  key={t.id}
                  whileTap={{ scale: 0.98 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => onUpdate({ temperament: t.id })}
                  className={`flex flex-col items-center text-center p-5 rounded-[20px] font-sans transition-all min-h-[100px] ${
                    isSelected
                      ? "border-2 shadow-md"
                      : "bg-card border border-border shadow-sm hover:shadow-md"
                  }`}
                  style={isSelected ? {
                    borderColor: goalColor ? `hsl(${goalColor})` : "hsl(var(--foreground) / 0.3)",
                    backgroundColor: goalColor ? `hsl(${goalColor} / 0.06)` : "hsl(var(--card))",
                  } : {}}
                >
                  
                  <span className="font-semibold text-sm text-foreground">{t.label}</span>
                  <span className="text-xs text-muted-foreground mt-0.5 leading-tight">{t.desc}</span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Что любит */}
        <div className="space-y-2">
          <label className="font-sans font-semibold text-foreground" style={{ fontSize: "16px" }}>
            Что особенно любит?
          </label>
          <p className="text-xs text-muted-foreground font-sans">Можно написать через запятую</p>
          <input
            type="text"
            value={state.themes}
            onChange={(e) => onUpdate({ themes: e.target.value })}
            placeholder="Динозавры, космос, феи, машинки..."
            className="w-full px-4 rounded-[20px] bg-card border text-foreground font-sans focus:outline-none transition-shadow placeholder:text-muted-foreground/50"
            style={{ height: "56px", borderColor: "#E6E2DB" }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = goalColor
                ? `0 0 0 3px hsl(${goalColor} / 0.15)`
                : "0 0 0 3px hsl(var(--ring) / 0.2)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = "none";
            }}
          />
        </div>

        {/* CTA */}
        <div className="space-y-3 pt-2">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={onNext}
            disabled={!canContinue}
            className="w-full py-4 rounded-[28px] bg-primary text-primary-foreground font-sans font-semibold text-lg disabled:opacity-40 transition-all"
          >
            Далее
          </motion.button>
          <p className="text-center text-xs text-muted-foreground/50 font-sans">
            Мы используем эти данные только для создания истории
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ChildProfile;
